import { memo, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Store04 from "@zenith/assets/icons/Store04";
import UserMultiple02 from "@zenith/assets/icons/UserMultiple02";
import { ButtonComponent } from "@zenith/components";
import { ACCESS } from "@zenith/data/constants";
import { DASHBOARD } from "@zenith/routes/paths";
import { takeReturnTo } from "@zenith/features/Authentication/utils/pendingReturnTo";
import { BusinessProfileControllerService } from "@zenith/services/user/services/BusinessProfileControllerService";
import type { BusinessProfileSummaryDTO } from "@zenith/services/user/models/BusinessProfileSummaryDTO";

interface LocationState {
  /** Sign-in/2FA already returns this list - avoids a redundant refetch. */
  profiles?: BusinessProfileSummaryDTO[];
}

/**
 * Shown after sign-in when the account can act in more than one business
 * context, so the user picks which one to enter.
 *
 * Node 73696:27185: a fixed Merchant / Team Member tab pair, not a list of
 * rows - BOAS-8730's own scope is a user who owns a business and is
 * separately an employee on one other business, never more than one of
 * each, so each tab maps to exactly one profile.
 */
const SelectProfilePage = memo(function SelectProfilePage() {
  const navigate = useNavigate();
  const location = useLocation() as { state: LocationState | null };
  const [profiles, setProfiles] = useState<BusinessProfileSummaryDTO[]>(
    location.state?.profiles ?? []
  );
  const [isLoading, setIsLoading] = useState(!location.state?.profiles);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Direct/bookmarked visit, or a refresh - sign-in's own profiles list
    // didn't come along for the ride, so re-fetch it.
    if (location.state?.profiles) return;

    let isCurrent = true;
    // This route sits outside TokenHOC, so the Axios interceptor that normally
    // attaches `Authorization` is not mounted here - the token is passed
    // explicitly, or the fetch answers 401 right after signing in.
    BusinessProfileControllerService.profiles(
      sessionStorage.getItem(ACCESS) ?? undefined
    )
      .then((response) => {
        if (!isCurrent) return;
        const list = response.data ?? [];
        setProfiles(list);
        // A parked destination (an invitation waiting to be accepted) wins
        // over the dashboard: nobody was asked to choose a profile for its own
        // sake.
        if (list.length < 2)
          navigate(takeReturnTo() ?? DASHBOARD, { replace: true });
      })
      .catch((error) => {
        if (!isCurrent) return;
        setErrorMessage(
          error?.body?.message ?? "We couldn't load your profiles."
        );
      })
      .finally(() => {
        if (isCurrent) setIsLoading(false);
      });
    return () => {
      isCurrent = false;
    };
  }, [navigate, location.state]);

  // BOAS-8814: the business-context claim's profile is MERCHANT (the owner's
  // own login; older tokens say ADMINISTRATOR) or EMPLOYEE (everyone else).
  // The live API returns MERCHANT, so both spellings are honoured - looking
  // only for ADMINISTRATOR hid the Merchant tab from every real owner.
  const merchantProfile = profiles.find(
    (p) => p.profile === "MERCHANT" || p.profile === "ADMINISTRATOR"
  );
  const teamMemberProfile = profiles.find((p) => p.profile === "EMPLOYEE");

  const [selectedKind, setSelectedKind] = useState<
    "merchant" | "teamMember" | null
  >(null);
  const activeKind =
    selectedKind ?? (merchantProfile ? "merchant" : "teamMember");
  const activeProfile =
    activeKind === "merchant" ? merchantProfile : teamMemberProfile;

  const handleContinue = () => {
    if (!activeProfile?.businessId) return;
    setErrorMessage("");
    setIsSubmitting(true);
    BusinessProfileControllerService.select(
      { businessId: activeProfile.businessId },
      sessionStorage.getItem(ACCESS) ?? undefined
    )
      .then(() => navigate(takeReturnTo() ?? DASHBOARD, { replace: true }))
      .catch((error) =>
        setErrorMessage(
          error?.body?.message ?? "We couldn't open that profile."
        )
      )
      .finally(() => setIsSubmitting(false));
  };

  const tabClass = (kind: "merchant" | "teamMember") =>
    activeKind === kind
      ? "flex h-[90px] w-[189px] flex-col items-center justify-center gap-1 rounded-xl border border-primary-300 bg-primary-300/[0.08] text-primary-300"
      : "flex h-[90px] w-[189px] flex-col items-center justify-center gap-1 rounded-xl border border-neutral-200 bg-white text-neutral-800";

  // This page renders inside AuthenticationLayout's <Outlet />, which already
  // supplies the promo panel (logo, carousel, copyright) on the left - no
  // need to rebuild it here, and doing so previously produced a second,
  // conflicting panel nested inside the layout's own content column.
  return (
    <div className="mx-3 my-10 flex w-full flex-1 flex-col items-center justify-center overflow-x-hidden md:mx-auto">
      <div className="flex w-full max-w-md flex-col items-center gap-10">
        <div className="text-center">
          <h1 className="text-2xl font-medium text-neutral-900 md:text-4xl">
            Sign in as
          </h1>
          <p className="mt-4 text-base text-neutral-600">
            Select the profile you want to access
          </p>
        </div>

        {isLoading && (
          <p className="text-center text-sm text-neutral-600">
            Loading your profiles…
          </p>
        )}

        <div className="flex items-start justify-center gap-10">
          {merchantProfile && (
            <button
              id="select-profile-merchant"
              type="button"
              onClick={() => setSelectedKind("merchant")}
              className={tabClass("merchant")}
            >
              <Store04 width={24} height={24} />
              <span className="text-sm font-medium">Merchant</span>
            </button>
          )}
          {teamMemberProfile && (
            <button
              id="select-profile-team-member"
              type="button"
              onClick={() => setSelectedKind("teamMember")}
              className={tabClass("teamMember")}
            >
              <UserMultiple02 width={24} height={24} />
              <span className="text-sm font-medium">Team Member</span>
            </button>
          )}
        </div>

        {errorMessage && (
          <p className="text-center text-sm text-red-500">{errorMessage}</p>
        )}

        <ButtonComponent
          id="select-profile-continue"
          type="primary"
          size="large"
          label="Continue"
          fullWidth
          disabled={!activeProfile?.businessId}
          isLoading={isSubmitting}
          onClick={handleContinue}
        />
      </div>
    </div>
  );
});

export default SelectProfilePage;
